import { supabaseClient } from "@/lib/supabase";
import { readEdgeFunctionErrorMessage } from "@/features/admin/utils/edgeFunctionError";
import { useMutation } from "@tanstack/react-query";

import { getLicensedPromCatalogueWarning, sanitiseFormDraft, type FormDraft } from "./formDraft";

export interface FormDraftResponse {
    draft: FormDraft;
    warnings: string[];
}

export interface FormDraftRequest {
    prompt: string;
    currentDraft?: FormDraft;
}

export function useDraftFormWithAi() {
    return useMutation({
        mutationFn: async ({ prompt, currentDraft }: FormDraftRequest): Promise<FormDraftResponse> => {
            const { data, error } = await supabaseClient.functions.invoke<FormDraftResponse & { error?: string }>(
                "public-ai-draft-form",
                { body: { prompt, ...(currentDraft ? { currentDraft } : {}) } }
            );

            if (error) {
                throw new Error(
                    await readEdgeFunctionErrorMessage(error, data, "Could not draft the form. Try again or discard.")
                );
            }

            const sanitised = sanitiseFormDraft(data?.draft);
            if (!sanitised.draft) {
                throw new Error(sanitised.warnings[0] ?? "Could not draft the form. Try again or discard.");
            }

            return {
                draft: sanitised.draft,
                warnings: [
                    ...new Set(
                        [
                            ...(Array.isArray(data?.warnings) ? data.warnings : []),
                            ...sanitised.warnings,
                            getLicensedPromCatalogueWarning(prompt),
                        ].filter((warning): warning is string => Boolean(warning))
                    ),
                ],
            };
        },
    });
}
